"use strict";
/* globals module */
/**
 * @module blogPostCommentRoute
 * @description Baasic Blog Comment Route Definition provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic blogPost Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
*/
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
var common_1 = require("../../common");
var contracts_1 = require("../../core/contracts");
var BlogPostCommentRoute = /** @class */ (function (_super) {
    tslib_1.__extends(BlogPostCommentRoute, _super);
    function BlogPostCommentRoute(appOptions) {
        var _this = _super.call(this, appOptions) || this;
        _this.appOptions = appOptions;
        _this.findRoute = 'blog/blog-post-comments/{?searchQuery,ids,from,to,publishedFrom,publishedTo,url,template,blogPostPostStatusIds,languageIds,blogPostSlugs,tagSlugs,page,rpp,sort,embed,fields}';
        _this.getRoute = 'blog/blog-post-comments/{id}/{?embed,fields}';
        _this.purgeRoute = 'blog/blog-post-comments/purge';
        _this.createRoute = 'blog/blog-post-comments';
        _this.updateRoute = 'blog/blog-post-comments/{id}';
        _this.deleteRoute = 'blog/blog-post-comments/{id}';
        _this.approveRoute = 'blog/blog-post-comments/{id}/approve';
        _this.flagRoute = 'blog/blog-post-comments/{id}/flag';
        _this.spamRoute = 'blog/blog-post-comments/{id}/spam';
        _this.reportRoute = 'blog/blog-post-comments/{id}/report';
        _this.unapproveRoute = 'blog/blog-post-comments/{id}/unapprove';
        _this.unflagRoute = 'blog/blog-post-comments/{id}/unflag';
        _this.unspamRoute = 'blog/blog-post-comments/{id}/unspam';
        _this.unreportRoute = 'blog/blog-post-comments/{id}/unreport';
        return _this;
    }
    /**
     * Parses find blogPost route which can be expanded with additional options. Supported items are:
     * - `searchQuery` - A string referencing blogPost properties using the phrase or BQL (Baasic Query Language) search.
     * - `page` - A value used to set the page number, i.e. to retrieve certain blogPost subset from the storage.
     * - `rpp` - A value used to limit the size of result set per page.
     * - `sort` - A string used to set the blogPost property to sort the result collection by.
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * - `startDate` - A value used to specify the blogPost creation, publish or archive date date starting from which blogPost resource collection should be returned.
     * - `endDate` - A value used to specify the blogPost creation, publish or archive date until (and including) which blogPost resource collection should be returned.
     * - `statuses` - Comma separated list of blogPost statuses that specify where search should be done (Allowed statuses: Published, Draft and Archived).
     * - `tags` - A value used to restrict the search to blogPost resources with these tags. Multiple tags should be comma separated.
     * @method
     * @param options A promise that is resolved once the find action has been performed.
     * @example blogPostCommentRoute.find.expand({searchQuery: '<search-phrase>'});
     **/
    BlogPostCommentRoute.prototype.find = function (options) {
        var opt = options || {};
        return _super.prototype.baseFind.call(this, this.findRoute, opt);
    };
    /**
     * Parses get blogPost route which must be expanded with the Id of the previously created blogPost resource in the system. Additional expand supported items are:
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * @method
     * @param id blogPost slug or id which uniquely identifies blogPost resource that needs to be retrieved.
     * @param options Options object that contains embed items.
     * @example blogPostCommentRoute.get({id: '<blogPost-id>'});
     **/
    BlogPostCommentRoute.prototype.get = function (id, options) {
        return _super.prototype.baseGet.call(this, this.getRoute, id, options);
    };
    /**
     * Parses purge blogPost route, this URI template doesn't expose any additional properties.
     * @method
     * @example blogPostCommentRoute.purge();
     **/
    BlogPostCommentRoute.prototype.purge = function () {
        return _super.prototype.baseDelete.call(this, this.purgeRoute, {});
    };
    /**
     * Parses create blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @example blogPostCommentRoute.create();
     **/
    BlogPostCommentRoute.prototype.create = function () {
        return _super.prototype.baseCreate.call(this, this.createRoute, {});
    };
    /**
     * Parses update blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be updated into the system.
     * @example blogPostCommentRoute.update(data);
     **/
    BlogPostCommentRoute.prototype.update = function (data) {
        return _super.prototype.baseUpdate.call(this, this.updateRoute, data);
    };
    /**
     * Parses delete blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be removed from the system.
     * @example blogPostCommentRoute.delete(data);
     **/
    BlogPostCommentRoute.prototype.delete = function (data) {
        return _super.prototype.baseDelete.call(this, this.deleteRoute, data);
    };
    /**
     * Parses approve blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be approved into the system.
     * @example blogPostCommentRoute.approve(data);
     **/
    BlogPostCommentRoute.prototype.approve = function (data) {
        return _super.prototype.baseUpdate.call(this, this.approveRoute, data);
    };
    /**
     * Parses flag blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be flaged into the system.
     * @example blogPostCommentRoute.flag(data);
     **/
    BlogPostCommentRoute.prototype.flag = function (data) {
        return _super.prototype.baseUpdate.call(this, this.flagRoute, data);
    };
    /**
     * Parses spam blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be marked as spam into the system.
     * @example blogPostCommentRoute.spam(data);
     **/
    BlogPostCommentRoute.prototype.spam = function (data) {
        return _super.prototype.baseUpdate.call(this, this.spamRoute, data);
    };
    /**
     * Parses report blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be reported into the system.
     * @example blogPostCommentRoute.report(data);
     **/
    BlogPostCommentRoute.prototype.report = function (data) {
        return _super.prototype.baseUpdate.call(this, this.reportRoute, data);
    };
    /**
     * Parses unapprove blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be unapproved into the system.
     * @example blogPostCommentRoute.unapprove(data);
     **/
    BlogPostCommentRoute.prototype.unapprove = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unapproveRoute, data);
    };
    /**
     * Parses unflag blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be unflaged into the system.
     * @example blogPostCommentRoute.unflag(data);
     **/
    BlogPostCommentRoute.prototype.unflag = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unflagRoute, data);
    };
    /**
     * Parses unspam blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be unspamed into the system.
     * @example blogPostCommentRoute.unspam(data);
     **/
    BlogPostCommentRoute.prototype.unspam = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unspamRoute, data);
    };
    /**
     * Parses unreport blogPost route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An blogPost object that needs to be unreported into the system.
     * @example blogPostCommentRoute.unreport(data);
     **/
    BlogPostCommentRoute.prototype.unreport = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unreportRoute, data);
    };
    var _a;
    BlogPostCommentRoute = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(contracts_1.TYPES.IAppOptions)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof contracts_1.IAppOptions !== "undefined" && contracts_1.IAppOptions) === "function" ? _a : Object])
    ], BlogPostCommentRoute);
    return BlogPostCommentRoute;
}(common_1.BaseRoute));
exports.BlogPostCommentRoute = BlogPostCommentRoute;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - [URI Template](https://github.com/Baasic/uritemplate-js) syntax enables expanding the Baasic route templates to Baasic REST URIs providing it with an object that contains URI parameters.
 - All end-point objects are transformed by the associated route service.
*/
